import React from 'react';

import { withProps, withRef } from '@udecode/cn';
import {
  useTableCellElement,
  useTableCellElementResizableState,
  useTableCellElementState,
} from '@udecode/plate-table';
import { ResizeHandle } from '@udecode/plate-resizable';

import { useTableCellElementResizable } from './useTableCellElementResizable';

export const translateBorderStyle = (props) => {
  return `${props.size}px ${props.style} ${props.color}`;
};

export const getCssTableCellHandle = ({ side }) => {
  return {
    backgroundColor: '#bea06e',
    borderRadius: '5px',
    position: 'absolute',
    top: '-12px',
    [side]: '-1.5px',
    height: 'calc(100% + 12px)',
    width: '2px',
  };
};

export const getCssTableCellResizeHandle = ({
  side,
  width = 10,
  startMargin = 0,
  endMargin = 0,
  zIndex = 40,
  style,
}) => {
  const isHorizontal = side === 'left' || side === 'right';
  const start = isHorizontal ? 'top' : 'left';
  const end = isHorizontal ? 'bottom' : 'right';
  const size = isHorizontal ? 'width' : 'height';

  return {
    position: 'absolute',
    [side]: -width / 2,
    [start]: startMargin,
    [end]: endMargin,
    [size]: width,
    zIndex,
    cursor: isHorizontal ? 'col-resize' : 'row-resize',
    ...style,
  };
};

export const TableCellElement = withRef(({ children, className, hideBorder, isHeader, style, ...props }, ref) => {
  const { element, attributes } = props;

  const {
    borders,
    colIndex,
    colSpan,
    hovered,
    hoveredLeft,
    isSelectingCell,
    readOnly,
    rowIndex,
    rowSize,
    selected,
  } = useTableCellElementState();
  const { props: cellProps } = useTableCellElement({ element: props.element });
  const resizableState = useTableCellElementResizableState({
    colIndex,
    colSpan,
    rowIndex,
  });
  const tableCellElementResizable = useTableCellElementResizable(resizableState);
  const { bottomProps, hiddenLeft, leftProps, rightProps } = tableCellElementResizable;

  const {
    bottom,
    left,
    right,
    top,
  } = borders || {};

  const _style = {
    backgroundColor: element.bgColor || 'transparent',
    borderTop: top && !hideBorder ? translateBorderStyle(top) : undefined,
    borderRight: right && !hideBorder ? translateBorderStyle(right) : undefined,
    borderBottom: bottom && !hideBorder ? translateBorderStyle(bottom) : undefined,
    borderLeft: left && !hideBorder ? translateBorderStyle(left) : undefined,
    ... selected && {
      backgroundColor: 'rgba(190, 160, 110, 0.45)',
    },
  };

  const Cell = isHeader ? 'th' : 'td';

  // eslint-disable quotes
  return (
    <Cell
      asChild
      className={className}
      ref={ref}
      {...cellProps}
      {...attributes}
      style={
        {
          '--cellBackground': element.background,
          ...style,
          ..._style,
        }
      }
    >
      <div
        style={{
          minHeight: rowSize,
          height: '100%',
          position: 'relative',
        }}
      >
        {children}
      </div>

      {!isSelectingCell && (
        <div
          className="group absolute top-0 size-full select-none"
          contentEditable={false}
          suppressContentEditableWarning
        >
          {!readOnly && (
            <>
              <ResizeHandle
                {...rightProps}
                style={getCssTableCellResizeHandle({ side: 'right' })}
              />
              <ResizeHandle
                {...bottomProps}
                style={getCssTableCellResizeHandle({ side: 'bottom' })}
              />
              {!hiddenLeft && (
                <ResizeHandle
                  {...leftProps}
                  style={getCssTableCellResizeHandle({ side: 'left' })}
                />
              )}

              {hovered && (
                <div
                  style={getCssTableCellHandle({ side: 'right' })}
                />
              )}
              {hoveredLeft && (
                <div
                  style={getCssTableCellHandle({ side: 'left' })}
                />
              )}
            </>
          )}
        </div>
      )}
    </Cell>
  );
  // eslint-enable quotes
});

TableCellElement.displayName = 'TableCellElement';

export const TableCellHeaderElement = withProps(TableCellElement, {
  isHeader: true,
});
